import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@progress/kendo-react-buttons";
import { useNavigate, useLocation } from "react-router-dom";
import { ClientRoutes, ReactQueryKeys } from "../../../../Constants/ClientRoutingConstants";
import { useState } from "react";
import moment from "moment";
import { useQuery } from "react-query";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../../Shared/Components/Cards/ErrorCard";
import { DeviceDetailsInfoTabs } from "../Components/DeviceDetailsInfoTabs";
import { DeviceDetailsInfoTiles } from "../Components/DeviceDetailsInfoTiles";
import healthDashboardApiService from "../Utilities/HealthDashboardApiService";
import { EventFilter } from "../../../Events/Utilities/EventsApiService";
import DeviceInfoReadDto from "../../../../Models/Dashboard/DeviceInfoReadDto";
import HeadUnitsFlowEventsReadDto from "../../../../Models/Dashboard/HeadUnitsFlowEventsReadDto";
import { enMessages } from "../../../../messages/en-US";
import { useLocalization } from "@progress/kendo-react-intl";
import { useDeviceTimelineQueries } from "../../../Shared/Hooks/useDeviceTimelineQueries";


export default function DeviceHealthDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const localizationService = useLocalization();
  // Get the device data passed through navigation state
  const deviceData = location.state?.deviceData;
  const deviceType = location.state?.deviceType;

  const handleBack = () => {
    navigate(ClientRoutes.HealthDashboardPage, {
      state: { 
        selectedTile: location.state?.selectedTile || "account"
      }
    });
  };

  // Set up date range for the last 7 days
  const [eventFilter, setEventFilter] = useState<EventFilter>({
    endDate: moment().endOf('day').toDate(),
    startDate: moment().subtract(7, 'days').startOf('day').toDate(),
  });

  const {
    isLoading: timelineLoading,
    isError: timelineError,
    deviceFeatures,
    deviceNotifications,
    eventData: endpointEventData,
  } = useDeviceTimelineQueries(
    deviceData?.serialNumber ?? '',
    eventFilter,
    {
      enabled: !!deviceData?.serialNumber,
      eventsEnabled: deviceType === 'endpoint' && !!deviceData?.serialNumber,
      keepPreviousData: true,
    },
  );

  const deviceInfoQuery = useQuery<DeviceInfoReadDto>([ReactQueryKeys.DeviceInfoQuery, deviceData?.serialNumber, eventFilter], async () => {
    return healthDashboardApiService.getDeviceInfoChart(eventFilter, deviceData?.serialNumber!);
  },
  {
    enabled: deviceType !== 'endpoint' && !!deviceData?.serialNumber,
    keepPreviousData: true
  }
);

  const headUnitFlowEventsQuery = useQuery<HeadUnitsFlowEventsReadDto[]>(
    ['headUnitFlowEvents', deviceData?.serialNumber, eventFilter],
    async () => {
      return healthDashboardApiService.getHeadUnitFlowEvents(deviceData?.id!, eventFilter);
    },
    {
      enabled: deviceType === 'headUnit' && !!deviceData?.id,
      keepPreviousData: true
    }
  );

  const deviceHealthData = {
    deviceData,
    deviceType,
    endpointFeature: deviceFeatures!,
    deviceInfo: deviceInfoQuery.data,
    endpointEventData,
    headUnitFlowEvents: headUnitFlowEventsQuery.data,
  };

  if (timelineLoading || deviceInfoQuery.isLoading || headUnitFlowEventsQuery.isLoading) {
    return <LoadingSpinner />;
  }
  if (timelineError || deviceInfoQuery.isError || headUnitFlowEventsQuery.isError) {
    return <ErrorCard />;
  }

  return (
    <div className='m-5'>
      <div className="flex items-center gap-4 mb-4">
        <Button
              onClick={handleBack}
              fillMode="clear"
            >
              <FontAwesomeIcon icon={faArrowLeft} size="lg" />
            </Button>
        <h2 className="gb-h2 bg-white">
          {localizationService.toLanguageString('custom.deviceHealthDetails', enMessages.custom.deviceHealthDetails)}
        </h2>
      </div>
      <DeviceDetailsInfoTiles deviceData={deviceData} deviceType={deviceType}/>
      <DeviceDetailsInfoTabs
        deviceHealthData={deviceHealthData}
        eventFilter={eventFilter}
        setEventFilter={setEventFilter}
        deviceSerialNumber={deviceData?.serialNumber}
        deviceId={deviceData?.id}
        deviceNotifications={deviceNotifications}
        deviceFeatures={deviceFeatures}
        />
    </div>
  );
}
